export type TimelineKind = "created" | "updated" | "opened" | "deploy";

export interface TimelineItem {
  readonly id: string;
  readonly at: string | null;
  readonly title: string;
  readonly detail: string | null;
  readonly kind: TimelineKind;
  /** Display name when known from membership; null = omit (no invented user). */
  readonly actor: string | null;
}

export interface TimelineSource {
  readonly createdAt: string | null;
  readonly updatedAt: string;
  readonly createdTitle: string;
  readonly updatedTitle: string;
  readonly opened: { readonly at: string; readonly title: string } | null;
  readonly extras: readonly TimelineItem[];
  readonly includeDeployPlaceholder: boolean;
}

const sameInstant = (left: string, right: string): boolean => {
  const a = new Date(left).getTime();
  const b = new Date(right).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) {
    return left === right;
  }
  return a === b;
};

const byTimeDesc = (left: TimelineItem, right: TimelineItem): number => {
  if (left.at === null && right.at === null) {
    return 0;
  }
  if (left.at === null) {
    return 1;
  }
  if (right.at === null) {
    return -1;
  }
  return new Date(right.at).getTime() - new Date(left.at).getTime();
};

/**
 * Derive a short timeline from existing created/updated timestamps.
 * Deploy is at most one honest placeholder — not a deploy system.
 */
export const buildTimeline = (source: TimelineSource): readonly TimelineItem[] => {
  const items: TimelineItem[] = [...source.extras];
  if (source.createdAt !== null) {
    items.push({
      id: "created",
      at: source.createdAt,
      title: source.createdTitle,
      detail: null,
      kind: "created",
      actor: null,
    });
  }
  if (source.createdAt === null || !sameInstant(source.createdAt, source.updatedAt)) {
    items.push({
      id: "updated",
      at: source.updatedAt,
      title: source.updatedTitle,
      detail: null,
      kind: "updated",
      actor: null,
    });
  }
  if (source.opened !== null) {
    items.push({
      id: "opened",
      at: source.opened.at,
      title: source.opened.title,
      detail: "Nesta sessão",
      kind: "opened",
      actor: null,
    });
  }
  if (source.includeDeployPlaceholder) {
    items.push({
      id: "deploy-placeholder",
      at: null,
      title: "Deploy",
      detail: "Ainda sem eventos de publicação — placeholder, sem sistema de deploy.",
      kind: "deploy",
      actor: null,
    });
  }
  return items.sort(byTimeDesc);
};

export interface TimelineDayGroup {
  readonly label: string;
  readonly items: readonly TimelineItem[];
}

/**
 * Group existing timeline rows by relative calendar day (Hoje / Ontem / Há N dias).
 * Undated placeholders (deploy) land in "Sem data".
 */
export const groupTimelineByDay = (
  items: readonly TimelineItem[],
  now: Date,
  labelFor: (iso: string, now: Date) => string,
): readonly TimelineDayGroup[] => {
  const groups: TimelineDayGroup[] = [];
  const undated: TimelineItem[] = [];
  const buckets = new Map<string, TimelineItem[]>();
  const order: string[] = [];

  for (const item of items) {
    if (item.at === null) {
      undated.push(item);
      continue;
    }
    const label = labelFor(item.at, now);
    const existing = buckets.get(label);
    if (existing === undefined) {
      buckets.set(label, [item]);
      order.push(label);
      continue;
    }
    existing.push(item);
  }

  for (const label of order) {
    const grouped = buckets.get(label);
    if (grouped === undefined) {
      continue;
    }
    groups.push({ label, items: grouped });
  }
  if (undated.length > 0) {
    groups.push({ label: "Sem data", items: undated });
  }
  return groups;
};
